import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { getBudgets, setBudgetPpdInfo } from '../../../../redux/slices/orderSheet';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { fCurrency, fNumber } from '../../../../utils/formatNumber';
import { BudgetBar, SubcategoryBudget } from './index';
import { postCategoryForm } from '../../../../api/category';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { updateUserSettings } from '../../../../redux/slices/user';
import AdjustBudgetDate from '../AdjustBudgetDate';

BudgetBox.propTypes = {
  cart: PropTypes.array,
  catId: PropTypes.number,
  facId: PropTypes.any,
  budgetDate: PropTypes.instanceOf(Date) || undefined,
  budgetData: PropTypes.object || undefined,
  open: PropTypes.bool,
  setOpen: PropTypes.func
};

export default function BudgetBox({
  cart,
  catId,
  facId,
  open,
  setOpen,
  budgetData = undefined,
  budgetDate = undefined,
}) {
  const { id = '' } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const [tabDropdownAnchor, setTabDropdownAnchor] = useState(null);
  const [canIncludePendingOrders, setCanIncludePendingOrders] = useState(false);
  const [isDisplayingPPDValues, setIsDisplayingPPDValues] = useState(false);
  const [isSavingPpdSetting, setIsSavingPpdSetting] = useState(false);

  const {
    data: { budgets, budgetsMessage },
  } = useSelector((state) => state.orderSheet);

  const { orderById } = useSelector((state) => state.orders);

  const {
    data: { pendingOrders, previousOrders },
  } = useSelector((state) => state.orders);

  const { user: { settings } } = useSelector((state) => state.user);

  useEffect(()=> {
    setIsDisplayingPPDValues(settings?.find((setting) => setting?.areaId === `DisplayBudgetPPD`)?.show || false);
  }, [settings])

  const saveIsDisplayingPPDVales = () => {
    setIsSavingPpdSetting(true);
    dispatch(updateUserSettings([{
      areaId: `DisplayBudgetPPD`,
      field: '',
      show: !isDisplayingPPDValues,
      style: '',
      value: null,
    }])).then(() => setIsSavingPpdSetting(false))
    .catch(() => {
      setIsSavingPpdSetting(false);
      enqueueSnackbar('Error saving PPD display setting', { variant: 'error' });
    });
    setIsDisplayingPPDValues(!isDisplayingPPDValues)
  };

  const subcategoryIds = budgets?.flatMap((item) =>
    item.subbudgets
      .filter((subcategory) => !subcategory.excludeFromMain)
      .map((subcategory) => subcategory.agoraSubcategoryId)
  );
  const calculateBudgetTotalAmount = (cart) => cart
    ?.filter(
      (item) =>
        item?.removedFromBudget !== true &&
        (!subcategoryIds ||
          !item?.agoraSubcategory ||
          item?.agoraSubcategoryId === 0 ||
          subcategoryIds?.includes(item?.agoraSubcategoryId) ||
          item?.agoraSubcategory?.id === 0 ||
          subcategoryIds?.includes(item?.agoraSubcategory?.id))
    )
    .map((item) => {
      if (Array.isArray(item?.budgetSourceData) && item.budgetSourceData.length > 0) {
        return item.budgetSourceData.reduce((sum, source, index) => {
          const price = item.budgetPrice[index] || 0;
          const quantity = item.budgetQty[index] || 0;
          const tax = item.budgetTax[index] || 0;
          const shipping = item.budgetShipping[index] || 0;
          return sum + (price * quantity + tax + shipping);
        }, 0);
      }
      return item.price * item.quantity;
    })
    .reduce((a, b) => a + b, 0);

  const [updatedBudgetDate, setUpdatedBudgetDate] = useState(
    budgetDate ??
    (orderById[id]?.budgetDate
      ? new Date(orderById[id]?.budgetDate)
      : new Date(new Date().toDateString()))
  );

  const getWeekRange = () => {
    const today = new Date(updatedBudgetDate);
    const start = startOfWeek(today);
    const end = endOfWeek(today);
    const startDate = start.getMonth() === today.getMonth() ? start : startOfMonth(today);
    const endDate = end.getMonth() === today.getMonth() ? end : endOfMonth(today);
    return { calendarStart: start, calendarEnd: end, start: startDate, end: endDate };
  };

  const getDaysLength = (start, end) => Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || '';
  let facilityId = facId;
  if (!facilityId) {
    facilityId = searchParams.get('facilityId');
  }
  const savedBudgetTab = settings?.find((setting) => setting?.areaId === BUDGET_TAB_SETTING)?.value;
  const [tab, setTab] = useState(() => parseBudgetTab(savedBudgetTab));
  const isInitialTabRender = useRef(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [censusOpen, setCensusOpen] = useState(false);
  const censusTextRef = useRef(null);
  const ppdTextRef = useRef(null);
  const remainingTextRef = useRef(null);
  const budgetTextRef = useRef(null);
  const categoryTextRef = useRef(null);
  const [showCensusOverflowTooltip, setShowCensusOverflowTooltip] = useState('');
  const [showPPDOverflowTooltip, setShowPPDOverflowTooltip] = useState('');
  const [showRemainingOverflowTooltip, setShowRemainingOverflowTooltip] = useState('');
  const [showBudgetOverflowTooltip, setShowBudgetOverflowTooltip] = useState('');
  const [showCategoryOverflowTooltip, setShowCategoryOverflowTooltip] = useState('');
  const [useCurrentCensus, setUseCurrentCensus] = useState(false);
  const [includeUnapproved, setIncludeUnapproved] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [updatedIcon, setUpdatedIcon] = useState(undefined);
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const budgetTotalAmount = useMemo(() => calculateBudgetTotalAmount(cart) || 0, [cart]);

  const { start, end, calendarStart, calendarEnd } = useMemo(() => getWeekRange(), [updatedBudgetDate]);
  const daysLength = useMemo(() => getDaysLength(start, end), [start, end]);
  const monthDaysLength = getDaysInMonth(updatedBudgetDate.getFullYear(), updatedBudgetDate.getMonth());

  useEffect(() => {
    dispatch(
      getBudgets({
        facilityId,
        includeUnapproved,
        useCurrentCensus,
        category: [categoryId],
        orderNumber: id || 0,
        tab,
        budgetDate
      })
    );
  }, [useCurrentCensus, includeUnapproved, facilityId, categoryId, id, updatedBudgetDate]);

  useEffect(() => {
    setTab(parseBudgetTab(savedBudgetTab));
  }, [savedBudgetTab]);

  useEffect(() => {
    if (isInitialTabRender.current) {
      isInitialTabRender.current = false;
      return;
    }
    if (tab > 1)
      dispatch(
        getBudgets({
          facilityId,
          includeUnapproved,
          useCurrentCensus,
          category: [categoryId],
          orderNumber: id || 0,
          tab,
          budgetDate
        })
      );
  }, [tab]);

  useEffect(() => {
    if (budgetDate) {
      setUpdatedBudgetDate(budgetDate);
    } else if (orderById[id]?.budgetDate) {
      setUpdatedBudgetDate(new Date(orderById[id].budgetDate));
    }
  }, [budgetDate, id, orderById[id]?.budgetDate]);

  useEffect(() => {
    const currentPendingOrder = pendingOrders.find((order) => order.id.toString() === id);
    if (currentPendingOrder) {
      const otherPendingOrders = pendingOrders.find(
        (order) =>
          order.id !== currentPendingOrder.id &&
          order.facilityId === currentPendingOrder.facilityId &&
          order.agoraCategoryId === currentPendingOrder.agoraCategoryId
      );
      if (otherPendingOrders) {
        setCanIncludePendingOrders(true);
      }
    } else if (orderById[id]) {
      const otherPendingOrders = pendingOrders.find(
        (order) => order.facilityId === orderById[id].facilityId && order.department === orderById[id].department
      );
      if (otherPendingOrders) setCanIncludePendingOrders(true);
    }
  }, [pendingOrders, previousOrders, categoryId, facilityId, id, orderById]);
  const handleTabChange = (newValue) => {
    setTab(newValue);
    if (`${newValue}` === savedBudgetTab) return;
    dispatch(updateUserSettings([{
      areaId: BUDGET_TAB_SETTING,
      field: '',
      show: true,
      style: '',
      value: `${newValue}`,
    }]));
  };

  const currentBudget = useMemo(
    () => budgets?.find((budget) => budget?.agoraCategoryId === catId),
    [budgetData, budgets, catId]
  );

  useEffect(() => setUseCurrentCensus(censusType === "Current"), [budgets]);

  const {
    agoraCategoryId,
    category,
    censusAmount,
    censusType,
    monthBudgetAmount,
    monthSpent,
    ppdAmount,
    flatAmount,
    subbudgets,
    weekBudgetAmount,
    weekSpent,
    calendarWeekBudgetAmount,
    calendarWeekSpent,
    yearBudgetAmount,
    yearSpent,
    quarterBudgetAmount,
    quarterSpent,
    quarterLabel,
    daysInQuarter,
    daysInYear,
    icon,
  } = currentBudget || {};
  const handleSetIcon = async () => {
    postCategoryForm({ id: agoraCategoryId, name: category, icon: updatedIcon })
      .then(() => enqueueSnackbar('Updated icon successfully', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Error updating icon', { variant: 'error' }));
  };

  const remainingAmount = useMemo(() => tab === 1
    ? weekBudgetAmount - weekSpent - budgetTotalAmount
    : tab === 2
      ? quarterBudgetAmount - quarterSpent - budgetTotalAmount
      : tab === 3
        ? yearBudgetAmount - yearSpent - budgetTotalAmount
        : tab === 4
          ? calendarWeekBudgetAmount - calendarWeekSpent - budgetTotalAmount
          : monthBudgetAmount - monthSpent - budgetTotalAmount
  , [tab, weekBudgetAmount, weekSpent, quarterBudgetAmount, quarterSpent, yearBudgetAmount, yearSpent, calendarWeekBudgetAmount, calendarWeekSpent, monthBudgetAmount, monthSpent, budgetTotalAmount]);

  const budgetAmount = useMemo(() => tab === 1
    ? weekBudgetAmount
    : tab === 2
      ? quarterBudgetAmount
    : tab === 3
      ? yearBudgetAmount
      : tab === 4
        ? calendarWeekBudgetAmount
        : monthBudgetAmount,
  [tab, weekBudgetAmount, quarterBudgetAmount, yearBudgetAmount, calendarWeekBudgetAmount, monthBudgetAmount]);

  useEffect(() => {
    let rafId = 0;

    const isOverflowing = (ref) => Boolean(ref.current && ref.current.scrollWidth > ref.current.clientWidth);

    const recomputeOverflowTooltips = () => {
      setShowPPDOverflowTooltip(isOverflowing(ppdTextRef));
      setShowCensusOverflowTooltip(isOverflowing(censusTextRef));
      setShowRemainingOverflowTooltip(isOverflowing(remainingTextRef));
      setShowBudgetOverflowTooltip(isOverflowing(budgetTextRef));
      setShowCategoryOverflowTooltip(isOverflowing(categoryTextRef));
    };

    const scheduleRecompute = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(recomputeOverflowTooltips);
    };

    scheduleRecompute();
    window.addEventListener('resize', scheduleRecompute);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleRecompute);
    };
  }, [budgets, tab, remainingAmount, censusAmount, budgetAmount, open]);

  const amountDaysInBudget = useMemo(() => {
    if (tab === 1) return daysLength;
    if(tab === 2) return daysInQuarter;
    if(tab === 3) return daysInYear;
    if(tab === 4) return 7;
    return monthDaysLength;
  }, [tab, daysLength, daysInQuarter, daysInYear, monthDaysLength]);

  const periodLabel = useMemo(() => {
    if (tab === 0) {
      return `${updatedBudgetDate.toLocaleString('default', {
        month: 'short',
      })} ${updatedBudgetDate.getFullYear()}`;
    }
    if (tab === 1) {
      return `${start.toLocaleString('default', {
        month: 'short',
        day: 'numeric',
      })} - ${end.toLocaleString('default', { month: 'short', day: 'numeric' })}`;
    }
    if (tab === 2) return quarterLabel;
    if (tab === 4) {
      return `${calendarStart.toLocaleString('default', {
        month: 'short',
        day: 'numeric',
      })} - ${calendarEnd.toLocaleString('default', { month: 'short', day: 'numeric' })}`;
    }
    return updatedBudgetDate.getFullYear();
  }, [tab, updatedBudgetDate, start, end, quarterLabel, calendarStart, calendarEnd]);

  useEffect(() => {
    if (catId) dispatch(setBudgetPpdInfo(catId, { censusAmount, amountDaysInBudget, periodLabel }));
  }, [catId, censusAmount, amountDaysInBudget, periodLabel, id]);

  if (!currentBudget) return null;

  return (
    <Box sx={{ width: '100%' }} id="box">

      <Card sx={{ p: 1 }} id="card">
        {setOpen && (
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            onClick={() => setOpen(!open)}
            sx={{ cursor: 'pointer', mb: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
              Budgets
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer' }} color="info.main">
              <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
            </Typography>
          </Grid>
        )}
        {(!setOpen || open) && (
          currentBudget ?
            <Grid container>
              <Grid container direction="row" justifyContent="space-between" alignItems="center" wrap="nowrap" sx={{minWidth: 0}} spacing={2}>
                {/* negative margin to counteract the padding on the emoji picker */}
                <Grid item sx={{ ml: -1, display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                  <Box sx={{ ml: 1, mr: .5, display: 'flex' }} >
                    <Iconify icon={icon} width={16} height={16} clickable={false} />
                  </Box>
                  <Tooltip title={category} disableHoverListener={!showCategoryOverflowTooltip} placement="top" arrow>
                    <Typography
                      sx={{
                        color: 'text.main',
                        fontSize: '14px',
                        mr: '3px',
                        fontWeight: 'bold',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      ref={categoryTextRef}
                    >
                      {category}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    title={canIncludePendingOrders ? 'Include pending orders' : 'No orders to include'}
                    placement="top"
                    arrow
                  >
                    <span>
                      <IconButton
                        sx={{
                          width: '14px',
                          height: '14px',
                          marginLeft: '2px',
                          padding: '3px',
                          backgroundColor: (theme) => (includeUnapproved ? 'info.main' : theme.palette.grey[500]),
                          borderColor: 'secondary.main',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              includeUnapproved ? theme.palette.info.dark : theme.palette.grey[600],
                          },
                          '&.Mui-disabled': {
                            backgroundColor: (theme) => theme.palette.grey[300],
                            color: '#fff',
                            opacity: 1,
                          },
                        }}
                        onClick={() => setIncludeUnapproved(!includeUnapproved)}
                        disabled={!canIncludePendingOrders}
                      >
                        <Iconify
                          icon="carbon:usage-included-use-case"
                          style={{ color: 'white' }}
                          sx={{
                            ' path': {
                              stroke: 'white',
                              strokeWidth: '2',
                              ml: '0.5',
                            },
                          }}
                        />
                      </IconButton>{' '}
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Button
                      variant="text"
                      color="info"
                      onClick={(e) => setTabDropdownAnchor(e.currentTarget)}
                      sx={{ textTransform: 'none', p: 0}}
                    >
                      {' '}
                      <Typography sx={{ fontSize: '13px' }}>
                        {periodLabel}
                      </Typography>
                      <Iconify
                        icon={tabDropdownAnchor ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                        width={16}
                        height={16}
                      />
                    </Button>
                    <Menu
                      anchorEl={tabDropdownAnchor}
                      open={Boolean(tabDropdownAnchor)}
                      onClose={() => setTabDropdownAnchor(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <MenuItem
                        selected={tab === 0}
                        onClick={() => {
                          handleTabChange(0);
                          setTabDropdownAnchor(null);
                        }}
                      >
                        This Month
                      </MenuItem>
                      <MenuItem
                        selected={tab === 1}
                        onClick={() => {
                          handleTabChange(1);
                          setTabDropdownAnchor(null);
                        }}
                      >
                        This Week
                      </MenuItem>
                      <MenuItem
                        selected={tab === 2}
                        onClick={() => {
                          handleTabChange(2);
                          setTabDropdownAnchor(null);
                        }}
                      >
                        This Quarter
                      </MenuItem>
                      <MenuItem
                        selected={tab === 3}
                        onClick={() => {
                          handleTabChange(3);
                          setTabDropdownAnchor(null);
                        }}
                      >
                        This Year
                      </MenuItem>
                      <MenuItem
                        selected={tab === 4}
                        onClick={() => {
                          handleTabChange(4);
                          setTabDropdownAnchor(null);
                        }}
                      >
                        Calendar Week
                      </MenuItem>
                    </Menu>
                    {id && <AdjustBudgetDate budgetDate={updatedBudgetDate} orderId={id} iconOnly />}
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={0.5}>
                <Grid item xs={12} sm={6}>
                  <ExcelBox>
                    <Tooltip title={`PPD: ${fCurrency(ppdAmount)}`} arrow disableHoverListener={!showPPDOverflowTooltip}>
                      <Typography color="secondary.main" sx={excelBoxTextStyle} ref={ppdTextRef}>
                        PPD:{' '}<Box component="span" sx={{fontWeight: 'bold'}}>{fCurrency(ppdAmount)}</Box>
                      </Typography>
                    </Tooltip>
                    <Tooltip title={isDisplayingPPDValues ? 'Hide PPD Budget' : 'Show PPD Budget'} arrow>
                      <Button
                        color="info"
                        sx={{...excelBoxButtonStyle, backgroundColor: isDisplayingPPDValues ? 'info.active' : 'transparent'}}
                        onClick={saveIsDisplayingPPDVales}
                        disabled={isSavingPpdSetting}
                      >
                        <Iconify icon="mdi:chart-box-outline" color="info" height={18} width={18}/>
                      </Button>
                    </Tooltip>
                  </ExcelBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ExcelBox>
                    <Tooltip title={`Remaining: ${fCurrency(remainingAmount)}`} arrow disableHoverListener={!showRemainingOverflowTooltip}>
                    <Typography color="secondary.main" sx={excelBoxTextStyle} ref={remainingTextRef}>
                      Remaining:{' '}
                      <Box component="span" sx={{fontWeight: 'bold'}}>
                        {fCurrency(remainingAmount)}{' '}
                      </Box>
                    </Typography>
                    </Tooltip>
                    {(() => {
                      const value =
                        tab === 1
                          ? weekBudgetAmount - weekSpent - budgetTotalAmount
                          : tab === 2
                            ? quarterBudgetAmount - quarterSpent - budgetTotalAmount
                            : tab === 3
                              ? yearBudgetAmount - yearSpent - budgetTotalAmount
                              : tab === 4
                                ? calendarWeekBudgetAmount - calendarWeekSpent - budgetTotalAmount
                                : monthBudgetAmount - monthSpent - budgetTotalAmount;

                      return value < 0 ? (
                        <Button color="info" sx={{ ...excelBoxButtonStyle, backgroundColor: censusOpen ? 'black' : 'transparent' }}>
                          <Iconify
                            icon="cuida:alert-outline"
                            width={14}
                            height={14}
                            sx={{
                              ml: 0.5,
                            }}
                            style={{ color: 'red' }}
                          />
                        </Button>
                      ) : null;
                    })()}
                  </ExcelBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ExcelBox
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setCensusOpen(true);
                    }}
                  >
                    <Tooltip title={`${censusType} Census: ${fNumber(censusAmount)}`} arrow disableHoverListener={!showCensusOverflowTooltip}>
                      <Typography color="secondary.main" sx={excelBoxTextStyle} ref={censusTextRef}>
                        {censusType} Census: <Box component="span" sx={{fontWeight: 'bold'}}>{fNumber(censusAmount)}</Box>
                      </Typography>
                    </Tooltip>
                    <Tooltip title="Select Census Type" arrow>
                      <Button color="info" sx={{...excelBoxButtonStyle, backgroundColor: censusOpen ? 'info.active' : 'transparent'}}>
                        <Iconify icon="ep:refresh" color="info" height={18} width={18} />
                      </Button>
                    </Tooltip>
                  </ExcelBox>
                  <Menu
                    open={censusOpen}
                    onClose={() => {
                      setAnchorEl(null);
                      setCensusOpen(false);
                    }}
                    anchorEl={anchorEl}
                    sx={{
                      '& .MuiList-root': {
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    }}
                  >
                    <MenuItem
                      sx={{
                        '&:hover': {
                          cursor: 'default',
                        },
                        '& .MuiTouchRipple-root': {
                          display: 'none',
                        },
                      }}
                    >
                      <>
                        <Button
                          size="small"
                          variant={useCurrentCensus ? 'outlined' : 'contained'}
                          sx={{ borderRadius: '1px' }}
                          color="secondary"
                          onClick={() => setUseCurrentCensus(false)}
                        >
                          Average
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          variant={useCurrentCensus ? 'contained' : 'outlined'}
                          sx={{ borderRadius: '1px' }}
                          onClick={() => setUseCurrentCensus(true)}
                        >
                          Current
                        </Button>
                      </>
                    </MenuItem>
                  </Menu>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ExcelBox
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setBudgetOpen(true);
                    }}
                  >
                    <Tooltip title={`Budget: ${fCurrency(budgetAmount)}`} arrow disableHoverListener={!showBudgetOverflowTooltip}>
                      <Typography color="secondary.main" sx={excelBoxTextStyle} ref={budgetTextRef}>
                        Budget:{' '}
                        <Box component="span" sx={{fontWeight: 'bold'}}>
                          {fCurrency(budgetAmount)}
                        </Box>
                      </Typography>
                    </Tooltip>
                    <Tooltip title="View Budget Calculation" arrow>
                      <Button color="info" sx={{...excelBoxButtonStyle, backgroundColor: budgetOpen ? 'info.active' : 'transparent'}}>
                        <Iconify icon={'uil:calculator'} color="info" height={18} width={18} />
                      </Button>
                    </Tooltip>
                  </ExcelBox>
                  <Menu
                    open={budgetOpen}
                    onClose={() => {
                      setAnchorEl(null);
                      setBudgetOpen(false);
                    }}
                    anchorEl={anchorEl}
                  >
                    <Box sx={{ p: 1, fontSize: '8px' }}>
                      <Typography fontSize="11px" color="secondary">{` Census = ${censusAmount}`}</Typography>
                      <Typography fontSize="11px" sx={{ color: (theme) => theme.palette.info.main }}>
                        {` PPD = ${fCurrency(ppdAmount)}`}
                      </Typography>
                      <Typography fontSize="11px" color="secondary">
                        {` ${tab === 1
                          ? 'Week'
                          : tab === 2
                            ? 'Quarter'
                            : tab === 3
                              ? 'Year'
                              : tab === 4
                                ? 'Calendar Week'
                                : 'Month'
                          } = ${amountDaysInBudget} days`}
                      </Typography>
                      <Typography fontSize="11px" sx={{ color: (theme) => theme.palette.info.main }}>
                        Flat Amount = $
                        {tab === 1
                          ? (flatAmount / monthDaysLength) * daysLength
                          : tab === 2
                            ? flatAmount * 3
                            : tab === 3
                              ? flatAmount * 12
                              : tab === 4
                                ? (flatAmount / monthDaysLength) * 7
                                : flatAmount}
                      </Typography>
                      <Divider sx={{ mt: 1, mb: 1 }} />
                      <Typography fontSize="11px" color="secondary" display="inline">
                        Census
                      </Typography>
                      <Typography fontSize="11px" display="inline">
                        {' x '}
                      </Typography>
                      <Typography fontSize="11px" sx={{ color: (theme) => theme.palette.info.main }} display="inline">
                        PPD
                      </Typography>
                      <Typography fontSize="11px" display="inline">
                        {' x '}
                      </Typography>
                      <Typography fontSize="11px" color="secondary" display="inline">
                        {tab === 1
                          ? 'Week'
                          : tab === 2
                            ? 'Quarter'
                            : tab === 3
                              ? 'Year'
                              : tab === 4
                                ? 'Calendar Week'
                                : 'Month'}
                      </Typography>
                      <Typography fontSize="11px" display="inline">
                        {' + '}
                      </Typography>
                      <Typography fontSize="11px" display="inline" sx={{ color: (theme) => theme.palette.info.main }}>
                        Flat Amount
                      </Typography>
                      <Typography fontSize="11px" display="inline">
                        {' = '}
                        {fCurrency(
                          tab === 1
                            ? weekBudgetAmount
                            : tab === 2
                              ? quarterBudgetAmount
                              : tab === 3
                                ? yearBudgetAmount
                                : tab === 4
                                  ? calendarWeekBudgetAmount
                                  : monthBudgetAmount
                        )}
                      </Typography>
                    </Box>
                  </Menu>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ my: 1 }}>
                <BudgetBar
                  includeUnapproved={includeUnapproved}
                  budget={
                    tab === 1
                      ? weekBudgetAmount
                      : tab === 2
                        ? quarterBudgetAmount
                        : tab === 3
                          ? yearBudgetAmount
                          : tab === 4
                            ? calendarWeekBudgetAmount
                            : monthBudgetAmount
                  }
                  total={
                    (tab === 1
                      ? weekSpent
                      : tab === 2
                        ? quarterSpent
                        : tab === 3
                          ? yearSpent
                          : tab === 4
                            ? calendarWeekSpent
                            : monthSpent) + budgetTotalAmount
                  }
                  budgetDate={updatedBudgetDate}
                  history={
                    tab === 1
                      ? weekSpent
                      : tab === 2
                        ? quarterSpent
                        : tab === 3
                          ? yearSpent
                          : tab === 4
                            ? calendarWeekSpent
                            : monthSpent
                  }
                  catId={catId}
                  facId={facId}
                  tab={tab}
                  isDisplayingPPDValues={isDisplayingPPDValues}
                  censusAmount={censusAmount}
                  amountDaysInBudget={amountDaysInBudget}
                />
              </Grid>
              <Grid item xs={12}>
                <SubcategoryBudget
                  subbudgets={subbudgets}
                  tab={tab}
                  cart={cart}
                  censusAmount={censusAmount}
                  daysLength={daysLength}
                  monthDaysLength={monthDaysLength}
                  dataRange={'Week'}
                  catId={catId}
                  facId={facId}
                  budgetDate={updatedBudgetDate}
                  includeUnapproved={includeUnapproved}
                  amountDaysInBudget={amountDaysInBudget}
                  isDisplayingPPDValues={isDisplayingPPDValues}
                />
              </Grid>
            </Grid> :
            <Box sx={{ p: 1, textAlign: 'center' }}>
              {budgetsMessage}
            </Box>
        )}
      </Card>
    </Box>
  );
}

// 0 = This Month, 1 = This Week, 2 = This Quarter, 3 = This Year, 4 = Calendar Week
const BUDGET_TAB_SETTING = 'BudgetTab';
const DEFAULT_BUDGET_TAB = 4;

const parseBudgetTab = (value) => {
  const parsed = parseInt(value, 10);
  return parsed >= 0 && parsed <= 4 ? parsed : DEFAULT_BUDGET_TAB;
};

const ExcelBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 30,
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#fff',
  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
  borderRadius: 4,
  boxShadow: 'none',
  padding: 6,
}));

const excelBoxTextStyle = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '12px',
}

const excelBoxButtonStyle = {
  padding: '2px',
  minWidth: 0
}
